import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { apiFetch } from "@/services/http/api-fetch";

type CohortEvent = {
  id: string;
  title: string;
  startsAt: string;
  zoomUrl: string;
  cohortName: string;
};

type Props = {
  courseId: string;
};

export function CohortLivePanel({ courseId }: Props) {
  const t = useTranslations("player");
  const locale = useLocale();
  const [events, setEvents] = useState<CohortEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    void (async () => {
      try {
        const res = await apiFetch(`/v1/courses/${courseId}/cohort-events`, {
          signal: controller.signal,
        });
        const json = await res.json();
        if (!active) return;
        if (res.ok) {
          setEvents(json.data.events as CohortEvent[]);
        }
        setLoading(false);
      } catch (err) {
        if (!active || (err as Error)?.name === "AbortError") return;
        setLoading(false);
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [courseId]);

  if (loading) {
    return (
      <p className="text-sm text-[var(--text-muted)]" data-testid="cohort-live-loading">
        {t("cohortLiveLoading")}
      </p>
    );
  }

  if (events.length === 0) {
    return null;
  }

  return (
    <section
      className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5"
      data-testid="cohort-live-panel"
    >
      <h2 className="text-lg font-medium">{t("cohortLiveTitle")}</h2>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{t("cohortLiveHint")}</p>
      <ul className="mt-4 space-y-3">
        {events.map((event) => (
          <li key={event.id} data-testid={`cohort-live-event-${event.id}`}>
            <p className="font-medium">{event.title}</p>
            <p className="text-sm text-[var(--text-muted)]">
              {event.cohortName} · {/* COMM-10: the app's language, not the browser's. */}
              {new Date(event.startsAt).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </p>
            <a
              href={event.zoomUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block rounded bg-[var(--ink-9)] px-3 py-1.5 text-sm text-white"
              data-testid={`cohort-live-join-${event.id}`}
            >
              {t("cohortLiveJoin")}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
